import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import type { BdcDialCode } from "../../../../api/bdc.api";
import { useBdcContactReview } from "../../../../hooks/vault/mutations/useBdcContactReview";
import { useBdcStatus } from "../../../../hooks/vault/queries/useBdcStatus";
import { useInvalidateVaultFolder } from "../../../../hooks/vault/queries/useVaultFolder";
import { useAppSelector } from "../../../../redux/hooks";
import {
  Status,
  type Folder,
  type Requirement,
} from "../../../../redux/interfaces/folder.types";
import {
  isBdcActorMismatch,
  decodeVaultJwtActor,
  isNativeBdcSupported,
} from "../../../../utils/vault/bdcActor";
import {
  isDeterministicBdcError,
  mapBdcAxiosError,
} from "../../../../exceptions/creditReportExceptions";
import { Button } from "../../../ui/button";
import { NipDialCodeSelect } from "../id-validation/NipDialCodeSelect";
import {
  hasReviewedNipDestination,
  hasValidIDData,
  isBdcNaturalPersonSubfolder,
  isDataValidationSubmittedWorkflowStep,
  isValidNipDestinationDialCode,
  normalizeNipPhoneNumber,
} from "../id-validation/idDataValidation";
import { BdcStatusCards } from "./BdcStatusCards";
import { isLegacyBdcCaptureRecoveryEligible } from "../../utils/requirementGrouping";

interface BdcContactReviewProps {
  folder: Folder;
  idRequirement: Requirement | undefined;
  bdcRequirement: Requirement;
}

/** Reviews only the invitation contact; approved identity/forms stay immutable. */
function BdcContactReviewContent({
  folder,
  idRequirement,
  bdcRequirement,
  jwtToken,
}: BdcContactReviewProps & { jwtToken: string | null }) {
  const subfolder = folder.subfolders?.find((candidate) =>
    candidate.requirements.some(
      (requirement) => requirement.id === bdcRequirement.id,
    ),
  );
  const isParticipantMismatch = isBdcActorMismatch(jwtToken, subfolder?.slug);
  const isDelegatedContact =
    decodeVaultJwtActor(jwtToken) === "advisor" ||
    (decodeVaultJwtActor(jwtToken) === "tenant" &&
      subfolder?.slug.includes("guarantor"));
  const contact = subfolder?.slug.includes("guarantor")
    ? folder.metadata?.guarantor
    : folder.metadata?.tenant;
  const email = contact?.email?.trim().toLowerCase() ?? "";
  const phone = normalizeNipPhoneNumber(contact?.phone_number ?? "");
  const confirmedData = idRequirement?.verification_data?.confirmed_data;
  const canReviewIdentity =
    hasValidIDData(confirmedData, {
      requireDialCode: false,
      requireStrictCurp: true,
      requireExteriorNumber: true,
      requireBirthDate: isBdcNaturalPersonSubfolder(subfolder?.slug),
    }) && isDataValidationSubmittedWorkflowStep(idRequirement?.workflow_step);
  const scope = `${folder.id}:${bdcRequirement.id}:${jwtToken}`;
  const [selection, setSelection] = useState<{
    scope: string;
    value: BdcDialCode;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejectedIdentitySnapshot, setRejectedIdentitySnapshot] = useState<
    string | null
  >(null);
  const identitySnapshot = JSON.stringify({
    workflow: idRequirement?.workflow_step,
    status: idRequirement?.status,
    data: {
      ...confirmedData,
      email: undefined,
      phone_number: undefined,
      dial_code: undefined,
      nip_destination_dial_code: undefined,
      nip_destination_reviewed: undefined,
    },
  });
  const submitting = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const statusQuery = useBdcStatus(bdcRequirement.id, {
    enabled: Boolean(jwtToken && subfolder && !isParticipantMismatch),
  });
  const mutation = useBdcContactReview(folder.id);
  const refreshFolder = useInvalidateVaultFolder(folder.id);
  const persistedDialCode =
    confirmedData?.nip_destination_dial_code ?? confirmedData?.dial_code;
  const dialCode =
    isDelegatedContact &&
    isValidNipDestinationDialCode(statusQuery.data?.dial_code)
      ? statusQuery.data.dial_code
      : selection?.scope === scope
        ? selection.value
        : isValidNipDestinationDialCode(persistedDialCode)
          ? persistedDialCode
          : isValidNipDestinationDialCode(statusQuery.data?.dial_code)
            ? statusQuery.data.dial_code
            : "+52";
  const canRecoverApprovedIdentity =
    statusQuery.data?.identity_recovery_eligible === true &&
    statusQuery.data.sic_source === "buro_de_credito" &&
    statusQuery.data.is_active === true &&
    statusQuery.data.status === "capturing_inputs";
  const isReviewed =
    hasReviewedNipDestination(confirmedData) && !canRecoverApprovedIdentity;
  const isTerminal = [
    Status.Valid,
    Status.PendingReview,
    Status.Invalid,
  ].includes(bdcRequirement.status);
  const isLocked =
    (isTerminal &&
      !isLegacyBdcCaptureRecoveryEligible(bdcRequirement, statusQuery.data)) ||
    statusQuery.data?.status !== "capturing_inputs";

  if (!jwtToken || !subfolder || isParticipantMismatch) {
    return (
      <BdcStatusCards
        variant="read_only"
        title="Contacto del participante"
        description="Abre el enlace de tu expediente para revisar el contacto de tu autorización."
      />
    );
  }

  if (statusQuery.isPending) {
    return (
      <div
        role="status"
        className="flex items-center justify-center gap-3 py-16 text-slate-600"
      >
        <Loader2 className="h-5 w-5 animate-spin" />
        Consultando destino del NIP…
      </div>
    );
  }

  if (statusQuery.isError || !statusQuery.data) {
    const statusError = mapBdcAxiosError(statusQuery.error);
    return (
      <BdcStatusCards
        variant="read_only"
        title="No pudimos consultar el contacto"
        description={statusError.message}
      >
        {!isDeterministicBdcError(statusError.code) && (
          <Button
            variant="outline"
            disabled={statusQuery.isFetching}
            onClick={() => void statusQuery.refetch()}
          >
            Reintentar
          </Button>
        )}
      </BdcStatusCards>
    );
  }

  if (
    (!canReviewIdentity && !canRecoverApprovedIdentity) ||
    rejectedIdentitySnapshot === identitySnapshot
  ) {
    return (
      <BdcStatusCards
        variant="read_only"
        title="Tu información necesita una revisión"
        description="Contacta a quien te compartió el expediente para revisar tu identificación y domicilio aprobados antes de continuar con Buró de Crédito."
      >
        <Button variant="outline" onClick={() => void refreshFolder()}>
          Actualizar expediente
        </Button>
      </BdcStatusCards>
    );
  }

  if (!email || !phone || statusQuery.data.email_matches_metadata === false) {
    return (
      <BdcStatusCards
        variant="read_only"
        title="Revisa el contacto de tu invitación"
        description="Contacta a quien te compartió el expediente para corregir el correo o teléfono de la invitación y su correspondencia con la autorización de Buró de Crédito."
      />
    );
  }

  const submit = async () => {
    if (submitting.current || isLocked) return;
    submitting.current = true;
    setError(null);
    try {
      await mutation.mutateAsync({
        requirementId: bdcRequirement.id,
        body: {
          email,
          phone_number: phone,
          dial_code: dialCode,
          nip_destination_reviewed: true,
        },
      });
      if (mounted.current)
        toast.success("Contacto para Buró de Crédito confirmado.");
    } catch (cause) {
      if (!mounted.current) return;
      const info = mapBdcAxiosError(cause);
      if (info.code === "BDC070") {
        setRejectedIdentitySnapshot(identitySnapshot);
        return;
      }
      // Another session may have consumed the inputs after our status read.
      if (info.code === "BDC081") {
        void statusQuery.refetch();
        void refreshFolder();
      }
      setError(info.message);
    } finally {
      submitting.current = false;
    }
  };

  return (
    <div className="mx-auto max-w-lg space-y-6 px-4 py-8 sm:px-6">
      <div className="space-y-3 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-[#28397a]">
          <Mail className="h-6 w-6" aria-hidden="true" />
        </div>
        <h3 className="text-xl font-semibold text-slate-900">
          Confirma tu contacto para Buró de Crédito
        </h3>
        <p className="text-sm leading-6 text-slate-600">
          El NIP inicial se enviará a tu correo. Tu teléfono se usará si
          solicitas el reenvío por SMS.
        </p>
      </div>
      {canRecoverApprovedIdentity && (
        <p className="text-sm leading-6 text-slate-600">
          Al confirmar el contacto, usaremos la identidad y el domicilio de tu
          formulario aprobado para esta autorización.
        </p>
      )}
      <dl className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-5">
        <div>
          <dt className="text-sm text-slate-500">Correo de la invitación</dt>
          <dd className="break-all font-medium text-slate-900">{email}</dd>
        </div>
        <div>
          <dt className="text-sm text-slate-500">Teléfono de la invitación</dt>
          <dd className="font-medium tabular-nums text-slate-900">{phone}</dd>
        </div>
      </dl>
      <div className="space-y-2">
        <label
          htmlFor="bdc-contact-dial-code"
          className="text-sm font-medium text-slate-700"
        >
          Lada del teléfono
        </label>
        <NipDialCodeSelect
          id="bdc-contact-dial-code"
          value={dialCode}
          disabled={isDelegatedContact || isLocked || mutation.isPending}
          onValueChange={(value) => setSelection({ scope, value })}
        />
      </div>
      {error && (
        <p
          role="alert"
          className="rounded-lg bg-red-50 p-3 text-sm text-red-700"
        >
          {error}
        </p>
      )}
      {isLocked ? (
        <p role="status" className="text-sm text-slate-600">
          La autorización ya fue iniciada. El contacto está protegido; continúa
          en el paso de Buró de Crédito.
        </p>
      ) : (
        <Button
          className="h-12 w-full gap-2 bg-[#28397a] hover:bg-[#1e2d5f]"
          disabled={mutation.isPending}
          onClick={() => void submit()}
        >
          {mutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle2 className="h-4 w-4" />
          )}
          {mutation.isPending ? "Guardando contacto…" : "Confirmar contacto"}
        </Button>
      )}
      {isReviewed && (
        <p role="status" className="text-sm text-green-700">
          Contacto confirmado. Puedes continuar con los siguientes pasos.
        </p>
      )}
      {mutation.isSuccess && !isReviewed && (
        <div role="status" className="space-y-2 text-sm text-slate-600">
          <p>Contacto guardado. Actualiza el expediente para continuar.</p>
          <Button variant="outline" onClick={() => void refreshFolder()}>
            Actualizar expediente
          </Button>
        </div>
      )}
      <p className="text-xs leading-5 text-slate-500">
        Si el correo o teléfono no son correctos, contacta a quien te compartió
        el expediente.
      </p>
    </div>
  );
}

export function BdcContactReview(props: BdcContactReviewProps) {
  const jwtToken = useAppSelector((state) => state.vault.jwtToken);
  const subfolder = props.folder.subfolders?.find((candidate) =>
    candidate.requirements.some(
      (requirement) => requirement.id === props.bdcRequirement.id,
    ),
  );
  if (!isNativeBdcSupported(props.bdcRequirement, subfolder)) {
    return (
      <BdcStatusCards
        variant="read_only"
        title="Autorización no disponible para este participante"
        description="La autorización de Buró de Crédito corresponde a los participantes principales del expediente."
      />
    );
  }
  return (
    <BdcContactReviewContent
      key={`${props.folder.id}:${props.bdcRequirement.id}:${jwtToken}`}
      {...props}
      jwtToken={jwtToken}
    />
  );
}
